import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle,
  faCircleNotch,
  faChartLine,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import Gauge from "./Gauge";

export default function DashboardGroup({
  group,
  sensors,
  updateSelectedForBarGraph,
  updateSelectedForLogs,
}) {
  const [selectedForTotalRunInfo, setSelectedForTotalRunInfo] = useState();
  const [pumpLogDateRef, setPumpLogDateRef] = useState(
    new Date().toLocaleString("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
  );

  useEffect(() => {
    const clockTimer = setInterval(() => {
      setPumpLogDateRef(
        new Date().toLocaleString("en-US", {
          timeZone: "America/New_York",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
      );
    }, 1000);

    return () => clearInterval(clockTimer);
  }, []);

  function formatRunTime(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const formattedHours = hours.toString().padStart(2, "0");
    const formattedMinutes = minutes.toString().padStart(2, "0");
    const formattedSeconds = seconds.toString().padStart(2, "0");

    if (formattedHours === "00") {
      return `${formattedMinutes}:${formattedSeconds}`;
    } else {
      return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    }
  }

  function renderDigitalSensorStatus(sensor) {
    if (sensor.type === "mci") {
      return (
        <>
          <div>{sensor.storedStatus}</div>
        </>
      );
    } else {
      let icon = "faCircle";
      let iconClassName = "statusCircle green";
      let message =
        sensor.type === "pump"
          ? sensor.dashboardStoppedMessage
          : sensor.dashboardInactiveMessage;

      let messageClassName = "";

      if (sensor.type === "pump") {
        if (sensor.running) {
          message = sensor.dashboardRunningMessage;
          messageClassName = "green";
        } else {
          icon = "faCircleNotch";
        }
      }

      if (sensor.type === "alarm") {
        if (sensor.active) {
          message = sensor.dashboardActiveMessage;
          if (sensor.greenWhenActive) {
            messageClassName = "green";
          } else {
            iconClassName = "statusCircle red";
            messageClassName = "red";
          }
        }
      }

      return (
        <>
          <div className="digitalSensorStatusContainer">
            <FontAwesomeIcon
              className={`${iconClassName}`}
              icon={icon === "faCircleNotch" ? faCircleNotch : faCircle}
            />
            <div className={`${messageClassName}`}>{message}</div>
          </div>
          {sensor.type === "pump" && (
            <div className="runInfoBox">
              <div className="runInfoBoxHeading">24hr Run Info</div>
              <div className="runInfoFlex">
                <div className="runInfoFlexItem">
                  Count:{" "}
                  {sensor.pumpLogDate === pumpLogDateRef
                    ? sensor.runCount
                    : "0"}
                </div>
                <div className="runInfoFlexItem">
                  Duration:{" "}
                  {sensor.pumpLogDate === pumpLogDateRef
                    ? formatRunTime(sensor.runTime)
                    : "00:00"}
                </div>
              </div>
              <FontAwesomeIcon
                className="pumpLogsButton"
                icon={faChartLine}
                onClick={() =>
                  updateSelectedForBarGraph({
                    id: sensor.id,
                    name: sensor.deviceDescription,
                  })
                }
              />
              {sensor.trackTotalRunInfo && (
                <FontAwesomeIcon
                  className="pumpLogsButton"
                  icon={faInfoCircle}
                  onClick={() => setSelectedForTotalRunInfo(sensor)}
                />
              )}
            </div>
          )}
        </>
      );
    }
  }

  function renderPressureSensor(sensor, currentValue, label) {
    let adjustment = 5;
    let labelClassName = "pressureSensorDisplayLabel";
    let valueClassName = "pressureSensorDisplayValue green";

    if (sensor.type === "tank") {
      adjustment = 1;
    }

    if (
      (sensor.enableLowSetPoint && currentValue <= sensor.lowSetPoint) ||
      (sensor.enableHighSetPoint && currentValue >= sensor.highSetPoint)
    ) {
      labelClassName = "pressureSensorDisplayLabel red";
      valueClassName = "pressureSensorDisplayValue red";
    } else if (
      (sensor.enableLowSetPoint &&
        currentValue <= sensor.lowSetPoint + adjustment) ||
      (sensor.enableHighSetPoint &&
        currentValue >= sensor.highSetPoint - adjustment)
    ) {
      labelClassName = "pressureSensorDisplayLabel yellow";
      valueClassName = "pressureSensorDisplayValue yellow";
    }

    return (
      <div className="pressureSensorDisplayContainer">
        <div className={`${labelClassName}`}>{label}</div>
        <div className={`${valueClassName}`}>{currentValue}</div>
        {sensor.type === "pressure" && (
          <FontAwesomeIcon
            className="logsBtn"
            icon={faChartLine}
            onClick={() =>
              updateSelectedForLogs({
                id: sensor.id,
                type: sensor.type,
                name: label,
              })
            }
          />
        )}
      </div>
    );
  }

  function renderGauge(
    currentValue,
    rangeStart,
    rangeEnd,
    useLowSetPoint,
    lowSetPoint,
    useHighSetPoint,
    highSetPoint,
    displayDescription,
    description,
    sensorID
  ) {
    return (
      <Gauge
        currentValue={currentValue}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        useLowSetPoint={useLowSetPoint}
        lowSetPoint={lowSetPoint}
        useHighSetPoint={useHighSetPoint}
        highSetPoint={highSetPoint}
        displayDescription={displayDescription}
        description={description}
        sensorID={sensorID}
        updateSelectedForLogs={updateSelectedForLogs}
      />
    );
  }

  function displayRunInfo() {
    return (
      <div className="runCountModalFlex">
        <div className="flexColumnCenter">
          <div className="runInfoModalHeading">Today</div>
          <div className="runInfoModalLabel">Count</div>
          <div className="runInfoModalValue">
            {selectedForTotalRunInfo.pumpLogDate === pumpLogDateRef
              ? selectedForTotalRunInfo.runCount
              : "0"}
          </div>
          <div className="runInfoModalLabel">Duration</div>
          <div className="runInfoModalValue">
            {selectedForTotalRunInfo.pumpLogDate === pumpLogDateRef
              ? formatRunTime(selectedForTotalRunInfo.runTime)
              : "00:00"}
          </div>
        </div>
        <div className="flexColumnCenter">
          <div className="runInfoModalHeading">Lifetime</div>
          <div className="runInfoModalLabel">Count</div>
          <div className="runInfoModalValue">
            {selectedForTotalRunInfo.totalRunCount}
          </div>
          <div className="runInfoModalLabel">Duration</div>
          <div className="runInfoModalValue">
            {formatRunTime(selectedForTotalRunInfo.totalRunTime)}
          </div>
        </div>
      </div>
    );
  }

  function renderMarkIIIAlarmStatus(runStatus, active, message) {
    let icon = "faCircle";
    let iconClassName = "statusCircle green";

    let messageClassName = "";

    if (runStatus) {
      if (active) {
        messageClassName = "green";
      } else {
        icon = "faCircleNotch";
      }
    } else {
      if (active) {
        iconClassName = "statusCircle red";
        messageClassName = "red";
      }
    }

    return (
      <>
        <div className="digitalSensorStatusContainer">
          <FontAwesomeIcon
            className={`${iconClassName}`}
            icon={icon === "faCircleNotch" ? faCircleNotch : faCircle}
          />
          <div className={`${messageClassName}`}>{message}</div>
        </div>
      </>
    );
  }

  return (
    <>
      {selectedForTotalRunInfo && (
        <div className="modalContainer">
          <div className="modalContent">
            {displayRunInfo()}
            <div
              onClick={() => setSelectedForTotalRunInfo(null)}
              className="closeModalButton"
            >
              Close
            </div>
          </div>
        </div>
      )}
      <div className="dashboardGroupContainer">
        <div className="dashboardGroupHeading">{group.name}</div>
        {sensors.length > 0 && (
          <>
            {sensors.map((sensor) => (
              <div key={sensor.id} className="dashboardGroupSensor">
                {(sensor.type === "pressure" || sensor.type === "tank") && (
                  <>
                    {sensor.displayAsGauge ? (
                      <>
                        {renderGauge(
                          sensor.storedValue,
                          sensor.rangeStart,
                          sensor.rangeEnd,
                          sensor.enableLowSetPoint,
                          sensor.lowSetPoint,
                          sensor.enableHighSetPoint,
                          sensor.highSetPoint,
                          sensor.displayDescriptionInGauge,
                          sensor.deviceDescription,
                          sensor.id
                        )}
                      </>
                    ) : (
                      <>
                        {renderPressureSensor(
                          sensor,
                          sensor.storedValue,
                          sensor.deviceDescription
                        )}
                      </>
                    )}
                  </>
                )}
                {(sensor.type === "pump" || sensor.type === "alarm") && (
                  <>
                    <div className="digitalSensorDescription">
                      {sensor.deviceDescription}
                    </div>
                    <>{renderDigitalSensorStatus(sensor)}</>
                  </>
                )}
                {sensor.type === "em3" && (
                  <>
                    <div className="dashboardGroupSensor">
                      <div className="digitalSensorDescription">Run Status</div>
                      <>
                        {renderMarkIIIAlarmStatus(
                          true,
                          sensor.motorRunning,
                          sensor.motorRunning ? "RUNNING" : "STOPPED"
                        )}
                      </>
                    </div>
                    <div className="dashboardGroupSensor">
                      <div className="digitalSensorDescription">
                        Phase Status
                      </div>
                      <>
                        {renderMarkIIIAlarmStatus(
                          false,
                          sensor.phaseReversal,
                          sensor.phaseReversal ? "PHASE REVERSAL" : "NORMAL"
                        )}
                      </>
                    </div>
                    <div className="dashboardGroupSensor">
                      <div className="digitalSensorDescription">
                        AC Power Status
                      </div>
                      <>
                        {renderMarkIIIAlarmStatus(
                          false,
                          sensor.acPowerLoss,
                          sensor.acPowerLoss ? "POWER LOSS" : "NORMAL"
                        )}
                      </>
                    </div>
                    {sensor.useMarkIIISystemPressure && (
                      <div className="dashboardGroupSensor">
                        {sensor.displayAsGauge ? (
                          <>
                            {renderGauge(
                              sensor.systemPressure,
                              sensor.rangeStart,
                              sensor.rangeEnd,
                              sensor.enableLowSetPoint,
                              sensor.lowSetPoint,
                              sensor.enableHighSetPoint,
                              sensor.highSetPoint,
                              false,
                              "",
                              sensor.id
                            )}
                          </>
                        ) : (
                          <>
                            {renderPressureSensor(
                              sensor,
                              sensor.systemPressure,
                              "PSI"
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
}
